import { useState } from 'react'
import { RefreshCw, TrendingDown, TrendingUp } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
  ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th, EmptyRow,
} from '@/components/erp/primitives'
import { AsyncMultiSelect, DateRange, FilterBar } from '@/shared/ui/filters'
import { BucketAxis, ChartFrame, RankedBars, Sparkbars } from '@/shared/ui/charts'
import { EmptyState, ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { listUnits } from '@/features/geography/api'
import { fmtDate, fmtNumber, fmtRelative } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import { analytics, recomputePerformance } from './api'
import {
  ROLES, ROLE_ORDER, TIER_ORDER, activityLabel, roleLabel, scoreNumber, tierLabel,
} from './vocab'
import { ComparePanel } from './ComparePanel'
import { RecomputePanel } from './RecomputePanel'

/**
 * Network analytics.
 *
 * ## One endpoint, not six
 *
 * Every panel here shares one date range and one filter set. Six endpoints would
 * drift the moment one of them forgot a filter — which is exactly how the
 * transfer statistics endpoint spent a release ignoring filters the list
 * honoured, and how a number that looks answerable turns out not to be.
 *
 * ## What is not here, and why
 *
 * **Correlation coefficients.** The phase plan asked for "correlations", and a
 * Pearson r over campaign data with no confidence interval and no sample-size
 * guard is a number people act on and it is noise. The distribution and the
 * per-unit table say what a correlation would have claimed, without pretending
 * to a precision the data does not have.
 *
 * ## Colour
 *
 * Bars encode with length and position. This app's categorical chart tokens fail
 * a colour-vision check on adjacent pairs — worst ΔE 3.1 under protanopia
 * against a floor of 6 — so hue here would be decoration some readers cannot
 * decode. The one place colour appears is the tier distribution, and every
 * segment carries its label and its count.
 */

const PRESETS = [
  { id: '7', label: '7 days', days: 7 },
  { id: '30', label: '30 days', days: 30 },
  { id: '90', label: '90 days', days: 90 },
]

function isoDaysAgo(days: number) {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10)
}

export function MobilizerAnalyticsPage() {
  const canRead = useCan('mobilizer:read')
  const [params, setParams] = useSearchParams()

  const preset = params.get('preset') ?? '30'
  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''
  const role = params.get('role') ?? ''
  const [unit, setUnit] = useState<{ value: string; label: string }[]>([])

  const patch = (next: Record<string, string | null>) => {
    const sp = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === '') sp.delete(k); else sp.set(k, v)
    }
    setParams(sp, { replace: true })
  }

  const effectiveFrom = from || isoDaysAgo(Number(preset) || 30)
  const effectiveTo = to || new Date().toISOString().slice(0, 10)
  const unitId = unit[0]?.value

  const data = useLoad(
    () => (canRead
      ? analytics({ from: effectiveFrom, to: effectiveTo, role: role || undefined, unitId })
      : Promise.resolve(null)),
    [canRead, effectiveFrom, effectiveTo, role, unitId])

  if (!canRead) return <Forbidden what="mobilizer analytics" />

  const d = data.data
  const median = d?.medianScore ?? null
  const prevMedian = scoreNumber(d?.previous?.medianScore)
  const activeCount = [role, unitId].filter(Boolean).length

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Performance analytics"
        description={d
          ? `${fmtDate(d.from)} to ${fmtDate(d.to)} · worked out ${d.computedAt ? fmtRelative(d.computedAt) : 'never'} · rules v${d.rulesVersion}`
          : 'Everything on this screen is read from the nightly rollup, never computed as you look at it.'}
        right={(
          <button type="button" className="erp-btn" aria-label="Refresh"
                  onClick={() => void data.reload()}>
            <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
          </button>
        )}
      />

      <Panel dense>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1" role="group" aria-label="Range">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={!from && !to && preset === p.id}
                onClick={() => patch({ preset: p.id, from: null, to: null })}
                className={cn('erp-btn',
                  !from && !to && preset === p.id && 'border-chocolate text-chocolate font-medium')}
              >
                {p.label}
              </button>
            ))}
          </div>
          <DateRange from={from} to={to}
                     onChange={(next) => patch({ from: next.from, to: next.to, preset: null })} />
        </div>
      </Panel>

      <FilterBar activeCount={activeCount}>
        <select className="erp-input" value={role} aria-label="Role"
                onChange={(e) => patch({ role: e.target.value })}>
          <option value="">All roles</option>
          {ROLE_ORDER.map((c) => <option key={c} value={c}>{ROLES[c].label}</option>)}
        </select>
        <AsyncMultiSelect
          label="Within"
          selected={unit}
          onChange={(next) => setUnit(next.slice(-1))}
          placeholder="Narrow to a county or ward"
          search={async (term) => {
            const found = await listUnits({ q: term } as never, 0, 10)
            return found.content.map((u) => ({ value: u.id, label: u.name, sub: u.code }))
          }}
        />
      </FilterBar>

      <ErrorStrip message={data.error} />

      {d?.rulesChangedInRange && (
        <Note tone="bad">
          The scoring weights changed inside this range. The trend below is not a like-for-like
          comparison, and no chart can make it one.
        </Note>
      )}

      {data.loading && !d ? (
        <Panel><LoadingBlock label="Reading the rollup…" /></Panel>
      ) : !d ? (
        <Panel>
          <EmptyState title="Nothing to show"
                      hint="The nightly job has not run yet, so there is no rollup to read." />
        </Panel>
      ) : (
        <>
          <KpiStrip>
            <MetricCard
              label="Active mobilizers"
              value={fmtNumber(d.activeMobilizers)}
              sub={`of ${fmtNumber(d.totalMobilizers)} on the roster`}
              delta={d.previous
                ? `${d.activeMobilizers - d.previous.activeMobilizers >= 0 ? '+' : ''}${d.activeMobilizers - d.previous.activeMobilizers} vs before`
                : undefined}
              deltaType={d.previous && d.activeMobilizers >= d.previous.activeMobilizers ? 'up' : 'down'}
              accent
            />
            <MetricCard
              label="Activities"
              value={fmtNumber(d.activitiesTotal)}
              sub={`${fmtNumber(d.activitiesFlagged)} flagged and not scored`}
              delta={d.previous
                ? `${d.activitiesTotal - d.previous.activitiesTotal >= 0 ? '+' : ''}${fmtNumber(d.activitiesTotal - d.previous.activitiesTotal)}`
                : undefined}
              deltaType={d.previous && d.activitiesTotal >= d.previous.activitiesTotal ? 'up' : 'down'}
            />
            <MetricCard
              label="Median score"
              value={median === null ? '—' : Math.round(median).toLocaleString('en-KE')}
              sub={median === null
                ? 'nothing computed yet — not zero'
                : prevMedian === null ? 'no earlier window' : (
                  <span className={cn('inline-flex items-center gap-1',
                    median >= prevMedian
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-red-700 dark:text-red-400')}>
                    {median >= prevMedian ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {Math.round(median - prevMedian)} vs the window before
                  </span>
                )}
            />
            <MetricCard
              label="Mean score"
              value={d.meanScore === null || d.meanScore === undefined
                ? '—' : Math.round(d.meanScore).toLocaleString('en-KE')}
              sub="the median above is the one to read when a few people are far ahead"
            />
            <MetricCard
              label="Tasks finished"
              value={fmtNumber(d.tasks.completed)}
              sub={`${fmtNumber(d.tasks.expired)} expired, ${fmtNumber(d.tasks.assigned + d.tasks.accepted + d.tasks.inProgress)} still open`}
            />
          </KpiStrip>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Activity per day"
                   description="Every recorded action in the range, bucketed in the deployment's timezone.">
              <Sparkbars points={d.activityDaily.map((p) => ({ label: p.day.slice(5), value: p.count }))} />
              <BucketAxis points={d.activityDaily.map((p) => ({ label: p.day.slice(5), value: p.count }))} />
            </Panel>

            <Panel title="People active per day"
                   description="Distinct mobilizers who recorded anything. A flat activity line with a falling headcount is a different problem from a falling one.">
              <Sparkbars points={d.activeMobilizersDaily.map((p) => ({ label: p.day.slice(5), value: p.count }))} />
              <BucketAxis points={d.activeMobilizersDaily.map((p) => ({ label: p.day.slice(5), value: p.count }))} />
            </Panel>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Panel title="What kind of work" description="Across the whole range.">
              <RankedBars
                points={Object.entries(d.byActivityType)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 10)
                  .map(([code, count]) => ({ label: activityLabel(code), value: count }))}
                emptyMessage="Nothing recorded in this range."
              />
            </Panel>

            <Panel title="By role" description="Head count, ordered by seniority rather than size.">
              <RankedBars
                points={ROLE_ORDER
                  .filter((r) => (d.byRole[r] ?? 0) > 0)
                  .map((r) => ({ label: roleLabel(r), value: d.byRole[r] ?? 0 }))}
                emptyMessage="Nobody in scope."
              />
            </Panel>

            <Panel title="By tier"
                   description="Tiers rank mobilizers by how much they have brought in. Where
                                the lines between tiers fall is a setting, so this shape moves
                                when somebody changes them.">
              <RankedBars
                points={TIER_ORDER
                  .filter((t) => (d.byTier[t] ?? 0) > 0)
                  .map((t) => ({ label: tierLabel(t), value: d.byTier[t] ?? 0 }))}
                emptyMessage="Nobody is tiered yet."
              />
            </Panel>
          </div>

          <Panel title="Score distribution"
                 description="Where the network actually sits. A long tail at the bottom is a coaching problem; a gap in the middle is a data problem.">
            <ChartFrame title="Mobilizers per score band">
              <Sparkbars
                points={d.scoreDistribution.map((b) => ({
                  label: `${b.from}–${b.to}`,
                  value: b.count,
                }))}
                emptyMessage="No scores computed yet."
              />
              <BucketAxis points={d.scoreDistribution.map((b) => ({
                label: `${b.from}`,
                value: b.count,
              }))} />
            </ChartFrame>
          </Panel>

          <Panel bodyClassName="p-0" title="By unit"
                 description="Capped at a hundred rows and ordered by score — a national query would otherwise return forty thousand to draw a chart of twelve.">
            <ErpTable head={<>
              <Th>Unit</Th>
              <Th className="text-right">Mobilizers</Th>
              <Th className="text-right">Activities</Th>
              <Th className="text-right">Each</Th>
              <Th className="text-right">Median score</Th>
            </>}>
              {d.byUnit.length === 0 ? (
                <EmptyRow colSpan={5} message="No unit has anybody in it yet." />
              ) : d.byUnit.map((u) => (
                <tr key={u.unitId} className="hover:bg-muted/40" data-unit={u.unitName ?? u.unitId}>
                  <Td><span className="font-medium">{u.unitName ?? u.unitId}</span></Td>
                  <Td className="text-right erp-metric">{fmtNumber(u.mobilizers)}</Td>
                  <Td className="text-right erp-metric">{fmtNumber(u.activities)}</Td>
                  <Td className="text-right erp-metric">
                    {u.mobilizers ? Math.round(u.activities / u.mobilizers) : '—'}
                  </Td>
                  <Td className="text-right erp-metric">
                    {scoreNumber(u.medianScore)?.toLocaleString('en-KE') ?? '—'}
                  </Td>
                </tr>
              ))}
            </ErpTable>
          </Panel>

          <ComparePanel from={effectiveFrom} to={effectiveTo} />

          <RecomputePanel
            from={effectiveFrom}
            to={effectiveTo}
            onDone={() => void data.reload()}
            recompute={recomputePerformance}
          />
        </>
      )}
    </div>
  )
}
